import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import { CheckCircle, Package, ShoppingBag, User, ArrowRight, Truck, Clock } from 'lucide-react';
import { trackPurchase } from '../../utils/metaPixel';

export default function CheckoutSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const { user } = useAuth();
  const [latestOrder, setLatestOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [animationDone, setAnimationDone] = useState(false);

  // Notify parent window (CheckoutPage iframe host) that payment succeeded
  useEffect(() => {
    window.parent?.postMessage({ type: 'PAYTR_PAYMENT_COMPLETE', status: 'success' }, '*');
  }, []);

  useEffect(() => {
    // Clear cart on success
    localStorage.removeItem('cartItems');
    clearCart();

    // Trigger animation after mount
    const timer = setTimeout(() => setAnimationDone(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const fetchLatestOrder = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }
      try {
        const { data } = await supabase?.from('orders')?.select('id, total_amount, status, created_at, shipping_address')?.eq('user_id', user?.id)?.eq('payment_status', 'paid')?.order('created_at', { ascending: false })?.limit(1)?.maybeSingle();

        setLatestOrder(data);

        // Fire Meta Pixel Purchase event once order data is available
        if (data?.id && data?.total_amount) {
          trackPurchase(data?.id, data?.total_amount, []);
        }
      } catch (err) {
        console.error('Error fetching order:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestOrder();
  }, [user]);

  const orderDate = latestOrder?.created_at
    ? new Date(latestOrder.created_at)?.toLocaleDateString('tr-TR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : new Date()?.toLocaleDateString('tr-TR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

  const shippingName = latestOrder?.shipping_address?.full_name ||
    latestOrder?.shipping_address?.ContactName || '';

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-2xl mx-auto px-4 py-12 pt-28">
        {/* Success Card */}
        <div
          className={`bg-white rounded-2xl shadow-lg p-8 text-center transition-all duration-700 ${
            animationDone ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {/* Animated Check Icon */}
          <div className="flex items-center justify-center mb-6">
            <div className="relative">
              <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle
                  className={`w-14 h-14 text-green-500 transition-all duration-500 ${
                    animationDone ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
                  }`}
                />
              </div>
              {/* Pulse ring */}
              <div className="absolute inset-0 rounded-full bg-green-200 animate-ping opacity-30" />
            </div>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Siparişiniz Alındı! 🎉
          </h1>
          <p className="text-gray-500 text-base mb-6">
            Ödemeniz başarıyla tamamlandı. Siparişiniz hazırlanmaya başlandı.
          </p>

          {/* Order Info Box */}
          {!loading && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-5 mb-6 text-left space-y-3">
              {latestOrder && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 flex items-center gap-2">
                    <Package className="w-4 h-4 text-green-600" />
                    Sipariş No
                  </span>
                  <span className="text-sm font-mono font-semibold text-gray-800">
                    #{latestOrder?.id?.slice(0, 8)?.toUpperCase()}
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-green-600" />
                  Sipariş Tarihi
                </span>
                <span className="text-sm font-medium text-gray-800">{orderDate}</span>
              </div>

              {latestOrder?.total_amount && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 flex items-center gap-2">
                    <ShoppingBag className="w-4 h-4 text-green-600" />
                    Toplam Tutar
                  </span>
                  <span className="text-sm font-bold text-gray-900">
                    ₺{parseFloat(latestOrder?.total_amount)?.toFixed(2)}
                  </span>
                </div>
              )}

              {shippingName && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 flex items-center gap-2">
                    <User className="w-4 h-4 text-green-600" />
                    Alıcı
                  </span>
                  <span className="text-sm font-medium text-gray-800">{shippingName}</span>
                </div>
              )}
            </div>
          )}

          {/* Delivery Info */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-8 flex items-start gap-3 text-left">
            <Truck className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-900 mb-1">Teslimat Bilgisi</p>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Siparişiniz 1-3 iş günü içinde kargoya verilecektir</li>
                <li>• E-posta adresinize onay maili gönderilecektir</li>
                <li>• Sipariş durumunuzu profilinizden takip edebilirsiniz</li>
              </ul>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => navigate('/official-store')}
              className="flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl font-semibold"
            >
              <ShoppingBag className="w-5 h-5" />
              Alışverişe Devam Et
            </Button>
            <Button
              onClick={() => navigate('/user-profile#orders')}
              variant="outline"
              className="flex-1 flex items-center justify-center gap-2 border-2 border-gray-200 hover:border-gray-300 text-gray-700 py-3 rounded-xl font-semibold"
            >
              <Package className="w-5 h-5" />
              Siparişlerim
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Bottom note */}
        <p className="text-center text-sm text-gray-400 mt-6">
          Herhangi bir sorun için{' '}
          <button
            onClick={() => navigate('/support-ticket-system-user-interface')}
            className="text-blue-500 hover:underline"
          >
            destek ekibimizle
          </button>{' '}
          iletişime geçebilirsiniz.
        </p>
      </div>
    </div>
  );
}
